import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { Loader2 } from "lucide-react";
import { ROLES, formatRole } from "@/constants/permissions";
import { PageLayout } from "@/components/layout/PageLayout";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { PageTitle } from "@/components/ui/page-title";

interface UserWithRoles {
  id: number;
  first_name?: string;
  last_name?: string;
  email: string;
  roles: { org_id: string; role: string; status?: string }[];
}

export default function Users() {
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingUser, setSavingUser] = useState<number | null>(null);
  const [userUpdates, setUserUpdates] = useState<Record<number, { role: string; status: string }>>(
    {}
  );
  const { toast } = useToast();
  const { currentOrganization } = useOrganizationContext();
  const { userProfile } = useAuth();

  const fetchUsers = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setLoading(true);
    try {
      const { data: profilesData, error: profilesError } = await supabase
        .from("user_profiles")
        .select("id, first_name, last_name, email");

      if (profilesError) throw profilesError;

      const { data: rolesData, error: rolesError } = await supabase
        .from("org_user_roles")
        .select("*")
        .eq("org_id", currentOrganization.id);

      if (rolesError) throw rolesError;

      const usersWithRoles: UserWithRoles[] = profilesData.map((profile) => {
        const userRoles = rolesData.filter((role) => role.user_id === profile.id);
        return {
          ...(profile as any),
          roles: userRoles,
        } as UserWithRoles;
      });

      const updates: Record<number, { role: string; status: string }> = {};
      usersWithRoles.forEach((u) => {
        const r = u.roles.find((rr) => rr.org_id === currentOrganization.id);
        updates[u.id] = {
          role: r?.role || "",
          status: r?.status || "active",
        };
      });

      setUserUpdates(updates);
      setUsers(usersWithRoles);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching users:", error);
      }
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load users",
      });
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const ownersCount = useMemo(
    () =>
      users.reduce((acc, u) => acc + (u.roles.some((r) => r.role === ROLES.ORG_OWNER) ? 1 : 0), 0),
    [users]
  );

  const handleSave = async (user: UserWithRoles) => {
    if (!currentOrganization || !userProfile) return;
    const update = userUpdates[user.id];
    if (!update) return;

    // Prevent downgrading last org owner
    if (
      user.roles.some((r) => r.role === ROLES.ORG_OWNER) &&
      update.role !== ROLES.ORG_OWNER &&
      ownersCount <= 1
    ) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Assign another org owner before downgrading.",
      });
      return;
    }

    setSavingUser(user.id);
    try {
      const existing = user.roles.find((r) => r.org_id === currentOrganization.id);
      if (existing) {
        const { error } = await supabase
          .from("org_user_roles")
          .update({ role: update.role, status: update.status, modified_by: Number(userProfile.id) })
          .eq("user_id", user.id)
          .eq("org_id", currentOrganization.id)
          .eq("role", existing.role);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("org_user_roles").insert({
          user_id: user.id,
          org_id: currentOrganization.id,
          role: update.role,
          status: update.status,
          created_by: Number(userProfile.id),
        });
        if (error) throw error;
      }
      toast({ title: "User updated" });
      fetchUsers();
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error updating user role:", error);
      }
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update user",
      });
    } finally {
      setSavingUser(null);
    }
  };

  if (loading) {
    return (
      <PageLayout headerButtons={<AdminHeader />}>
        <div>Loading users...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout headerButtons={<AdminHeader />}>
      <PageTitle className="mb-6">User Management</PageTitle>
      <Table>
        <TableCaption>A list of your registered users.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.id}</TableCell>
              <TableCell>
                {user.first_name} {user.last_name}
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Select
                  value={userUpdates[user.id]?.role}
                  onValueChange={(val) =>
                    setUserUpdates((prev) => ({
                      ...prev,
                      [user.id]: { ...prev[user.id], role: val },
                    }))
                  }
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  {/* Platform-admin tool (Franklin staff): can set any real org
                      role directly. Org owners/admins manage members via
                      Organization Settings, not here. */}
                  <SelectContent>
                    <SelectItem value={ROLES.ORG_OWNER}>{formatRole(ROLES.ORG_OWNER)}</SelectItem>
                    <SelectItem value={ROLES.ORG_ADMIN}>{formatRole(ROLES.ORG_ADMIN)}</SelectItem>
                    <SelectItem value={ROLES.SUBCONTRACTOR}>
                      {formatRole(ROLES.SUBCONTRACTOR)}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Select
                  value={userUpdates[user.id]?.status}
                  onValueChange={(val) =>
                    setUserUpdates((prev) => ({
                      ...prev,
                      [user.id]: { ...prev[user.id], status: val },
                    }))
                  }
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSave(user)}
                  disabled={savingUser === user.id}
                >
                  {savingUser === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PageLayout>
  );
}
